//TODO REI need to convert DB constants to enum
import axios, { Canceler } from 'axios';
import { orderBy } from 'lodash';
import React, { useContext } from 'react';
import { connect, DispatchProp } from 'react-redux';

import { SourceAbbr } from '@dndbeyond/character-common-components/es';
import {
    Checkbox,
    Collapsible,
    CreatureBlockBasic,
    HtmlContent,
    LoadingPlaceholder,
    Select,
} from '@dndbeyond/character-components/es';
import {
    ApiAdapterPromise,
    ApiAdapterRequestConfig,
    ApiAdapterResponse,
    ApiAdapterUtils,
    ApiResponse,
    CharacterTheme,
    Constants,
    Creature,
    CreatureGroupRulesLookup,
    CreatureRule,
    CreatureRuleUtils,
    CreatureUtils,
    DefinitionPool,
    DefinitionPoolUtils,
    EntitledEntity,
    Extra,
    ExtrasManager,
    ExtraUtils,
    HelperUtils,
    MonsterDefinitionContract,
    RuleData,
    RuleDataUtils,
    rulesEngineSelectors,
    serviceDataActions,
    serviceDataSelectors,
    SnippetData,
    Vehicle,
    VehicleDefinitionContract,
    VehicleUtils,
} from '@dndbeyond/character-rules-engine/es';

import { SidebarHeader } from '../../../components/Sidebar';
import VehicleBlock from '../../../components/VehicleBlock';
import { ThemeButton } from '../../../components/common/Button';
import { ExtrasManagerContext } from '../../../managers/ExtrasManagerContext';
import * as appEnvSelectors from '../../../selectors/appEnv';
import * as apiCreatorSelectors from '../../../selectors/composite/apiCreator';
import { SharedAppState } from '../../../stores/typings';
import {
    AppLoggerUtils,
    AppNotificationUtils,
    ComponentUtils,
    FilterUtils,
    GD_VehicleBlockProps,
    TypeScriptUtils,
} from '../../../utils';
import ExtraManagePaneAddListing from './ExtraManagePaneAddListing';
import ExtraManagePaneCurrentListing from './ExtraManagePaneCurrentListing';

interface CollapsibleComponentProps {
    metaItems: Array<React.ReactNode>;
    heading: string | null;
}
interface CreatureBlockProps {
    creature: Creature;
    ruleData: RuleData;
}
export interface PagedListingProps {
    key: number | string;
    extra: Extra;
    onAdd: (extra: Extra, quantity: number) => void;
    collapsibleComponentProps: CollapsibleComponentProps;
    ContentComponent: React.ComponentType<any>;
    contentComponentProps: GD_VehicleBlockProps | CreatureBlockProps;
    showQuantity?: boolean;
}

enum DataRetrievalStatusEnum {
    NONE = 'NONE',
    LOADING = 'LOADING',
    LOADED = 'LOADED',
}

interface Props extends DispatchProp {
    ruleData: RuleData;
    loadAvailableMonsters: (
        additionalConfig?: Partial<ApiAdapterRequestConfig>,
    ) => ApiAdapterPromise<ApiResponse<Array<MonsterDefinitionContract>>>;
    loadAvailableVehicles: (
        additionalConfig?: Partial<ApiAdapterRequestConfig>,
    ) => ApiAdapterPromise<ApiResponse<EntitledEntity<VehicleDefinitionContract>>>;
    pageSize: number;
    creatureRules: Array<CreatureRule>;
    creatureGroupRulesLookup: CreatureGroupRulesLookup;
    isReadonly: boolean;
    snippetData: SnippetData;
    definitionPool: DefinitionPool;
    theme: CharacterTheme;
    extras: Array<Extra>;
    extrasManager: ExtrasManager;
}

interface DataState {
    vehicles: Array<Vehicle>;
}
interface State extends DataState {
    selectedGroup: number | null;
    creatureDefinitions: Array<MonsterDefinitionContract>;
    creatures: Array<Creature>;
    ruleCreatures: Array<Creature>;
    dataStatus: DataRetrievalStatusEnum;
    currentPage: number;
    filterQuery: string;
    filterRules: boolean;
    filterChallengeMin: number | null;
    filterChallengeMax: number | null;
    isCurrentExtrasCollapsed: boolean;
}
class ExtraManagePane extends React.PureComponent<Props, State> {
    static defaultProps = {
        pageSize: 20,
    };

    loadMonstersCanceler: null | Canceler = null;
    loadVehiclesCanceler: null | Canceler = null;

    constructor(props: Props) {
        super(props);

        this.state = {
            selectedGroup: null,
            creatureDefinitions: [],
            creatures: [],
            ruleCreatures: [],
            dataStatus: DataRetrievalStatusEnum.NONE,
            currentPage: 0,
            filterQuery: '',
            filterRules: true,
            filterChallengeMin: null,
            filterChallengeMax: null,
            isCurrentExtrasCollapsed: false,
            ...this.generateVehicleStateData(props),
        };
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        const { definitionPool } = this.props;

        if (definitionPool !== prevProps.definitionPool) {
            this.setState(this.generateVehicleStateData(this.props));
        }
    }

    componentDidMount() {
        const { dispatch, loadAvailableMonsters, loadAvailableVehicles } = this.props;

        this.setState({
            dataStatus: DataRetrievalStatusEnum.LOADING,
        });

        axios
            // forced to do any because of stupid typings
            .all<ApiAdapterResponse<ApiResponse<any>>>([
                loadAvailableMonsters({
                    cancelToken: new axios.CancelToken((c) => {
                        this.loadMonstersCanceler = c;
                    }),
                }),
                loadAvailableVehicles({
                    cancelToken: new axios.CancelToken((c) => {
                        this.loadVehiclesCanceler = c;
                    }),
                }),
            ])
            .then(([monsterResponse, vehicleResponse]) => {
                let vehicleDefinitions =
                    ApiAdapterUtils.getResponseData<EntitledEntity<VehicleDefinitionContract>>(vehicleResponse);
                if (vehicleDefinitions !== null && vehicleDefinitions.definitionData.length > 0) {
                    dispatch(
                        serviceDataActions.definitionPoolAdd(
                            vehicleDefinitions.definitionData,
                            vehicleDefinitions.accessTypes,
                        ),
                    );
                }

                let monsterResponseDefinitions =
                    ApiAdapterUtils.getResponseData<Array<MonsterDefinitionContract>>(monsterResponse);
                let creatureDefinitions: Array<MonsterDefinitionContract> = [];
                if (monsterResponseDefinitions !== null) {
                    creatureDefinitions = [...monsterResponseDefinitions];
                }

                this.setState((prevState) => {
                    let transformedCreatures = this.transformLoadedItems(creatureDefinitions, prevState.selectedGroup);

                    return {
                        dataStatus: DataRetrievalStatusEnum.LOADED,
                        creatureDefinitions,
                        creatures: transformedCreatures,
                        ruleCreatures: this.getCreaturesFilteredByRules(transformedCreatures, prevState.selectedGroup),
                    };
                });
                this.loadMonstersCanceler = null;
                this.loadVehiclesCanceler = null;
            })
            .catch(AppLoggerUtils.handleAdhocApiError);
    }

    componentWillUnmount(): void {
        if (this.loadMonstersCanceler !== null) {
            this.loadMonstersCanceler();
        }
        if (this.loadVehiclesCanceler !== null) {
            this.loadVehiclesCanceler();
        }
    }

    generateVehicleStateData = (props: Props): DataState => {
        const { definitionPool, ruleData } = props;

        let vehicleDefinitions = DefinitionPoolUtils.getTypedDefinitionList(
            Constants.DefinitionTypeEnum.VEHICLE,
            definitionPool,
        );

        let vehicles = vehicleDefinitions
            .map((vehicleDef) => VehicleUtils.simulateVehicle(vehicleDef, definitionPool, ruleData))
            .filter(TypeScriptUtils.isNotNullOrUndefined);

        return {
            vehicles,
        };
    };

    getLastPageIdx = <T extends any>(filteredItems: Array<T>): number => {
        const { pageSize } = this.props;

        return Math.ceil(filteredItems.length / pageSize) - 1;
    };

    getFilteredCreatures = (): Array<Creature> => {
        const { filterQuery, filterChallengeMin, filterChallengeMax } = this.state;

        let creatures = this.getCreatures();
        let filteredCreatures = creatures.filter((creature) => {
            if (
                filterQuery !== '' &&
                !FilterUtils.doesQueryMatchData(
                    filterQuery,
                    CreatureUtils.getName(creature),
                    CreatureUtils.getSearchTags(creature),
                )
            ) {
                return false;
            }

            let challengeInfo = CreatureUtils.getChallengeInfo(creature);
            const hideCR = CreatureUtils.getHideCR(creature);
            // Treat hide Challenge Rating like a 0 for challenge rating values
            if (hideCR) {
                if (filterChallengeMin !== null && 0 < filterChallengeMin) {
                    return false;
                }
                if (filterChallengeMax !== null && 0 > filterChallengeMax) {
                    return false;
                }
            } else {
                if (filterChallengeMin !== null && challengeInfo && challengeInfo.value < filterChallengeMin) {
                    return false;
                }
                if (filterChallengeMax !== null && challengeInfo && challengeInfo.value > filterChallengeMax) {
                    return false;
                }
            }

            return true;
        });

        return orderBy(filteredCreatures, (creature) => CreatureUtils.getName(creature));
    };

    transformLoadedItems = (
        creatureDefinitions: Array<MonsterDefinitionContract>,
        groupId: number | null,
    ): Array<Creature> => {
        const { ruleData } = this.props;

        if (groupId === null) {
            return [];
        }

        return creatureDefinitions.map((definition) => CreatureUtils.simulateCreature(definition, groupId, ruleData));
    };

    getCreaturesFilteredByRules = (creatures: Array<Creature>, groupId: number | null): Array<Creature> => {
        const { creatureRules, extrasManager } = this.props;

        let groupCreatures = extrasManager.getCreaturesFilteredByGroupFlags(creatures, groupId);
        let filteredCreatures: Array<Creature> = [];
        let unmatchedCreatures: Array<Creature> = [...groupCreatures];
        let hasRules: boolean = false;

        creatureRules.forEach((rule) => {
            if (groupId === null || !CreatureRuleUtils.isRuleGroup(rule, groupId)) {
                return;
            }

            let testableCreatures: Array<Creature> = [...unmatchedCreatures];
            unmatchedCreatures = [];
            testableCreatures.forEach((creature) => {
                if (CreatureRuleUtils.isValidCreature(creature, rule)) {
                    filteredCreatures.push(creature);
                } else {
                    unmatchedCreatures.push(creature);
                }
            });

            hasRules = true;
        });

        return hasRules ? filteredCreatures : groupCreatures;
    };

    getCreatures = (): Array<Creature> => {
        const { creatures, ruleCreatures, filterRules } = this.state;

        return filterRules ? ruleCreatures : creatures;
    };

    getVehicles = (): Array<Vehicle> => {
        const { vehicles } = this.state;

        return orderBy(vehicles, (vehicle) => VehicleUtils.getName(vehicle));
    };

    getCreateNames = (name: string, quantity: number): Array<string | null> => {
        let names: Array<string | null> = [];
        if (quantity > 1) {
            for (let i = 1; i <= quantity; i++) {
                names.push(`${name} ${i}`);
            }
        } else {
            names.push(null);
        }
        return names;
    };

    isFiltered = (): boolean => {
        const { filterQuery, filterRules } = this.state;

        if (filterRules) {
            return true;
        }

        if (filterQuery) {
            return true;
        }

        return false;
    };

    handlePageMore = (): void => {
        this.setState((prevState) => ({
            currentPage: prevState.currentPage + 1,
        }));
    };

    handleQueryChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
        const query = evt.target.value;

        this.setState({
            filterQuery: query,
            currentPage: 0,
        });
    };

    handleChallengeMinChange = (value: string): void => {
        this.setState({
            filterChallengeMin: HelperUtils.parseInputFloat(value),
        });
    };

    handleChallengeMaxChange = (value: string): void => {
        this.setState({
            filterChallengeMax: HelperUtils.parseInputFloat(value),
        });
    };

    handleRawToggle = (): void => {
        this.setState((prevState) => ({
            filterRules: !prevState.filterRules,
            currentPage: 0,
        }));
    };

    handleSelectedGroupChange = (value: string): void => {
        const { extrasManager } = this.props;

        let selectedGroup = HelperUtils.parseInputInt(value, null);

        this.setState((prevState) => {
            let transformedCreatures: Array<Creature> = this.transformLoadedItems(
                prevState.creatureDefinitions,
                selectedGroup,
            );

            let ruleCreatures: Array<Creature> = transformedCreatures;
            if (selectedGroup !== null && selectedGroup >= 0) {
                ruleCreatures = this.getCreaturesFilteredByRules(transformedCreatures, selectedGroup);
            }

            //Reset Challenge Rating filter for Sidekick group
            let filterChallengeMin = prevState.filterChallengeMin;
            let filterChallengeMax = prevState.filterChallengeMax;
            if (selectedGroup !== null && extrasManager.hack__isSidekickGroup(selectedGroup)) {
                filterChallengeMin = null;
                filterChallengeMax = null;
            }

            return {
                ruleCreatures,
                creatures: transformedCreatures,
                selectedGroup,
                isCurrentExtrasCollapsed: true,
                filterChallengeMax,
                filterChallengeMin,
            };
        });
    };

    handleCreateExtra = (extra: Extra, quantity: number): void => {
        const { extrasManager } = this.props;
        const { selectedGroup } = this.state;

        extrasManager.handleAdd(
            { extra, quantity, selectedGroup },
            AppNotificationUtils.handleExtraCreateAccepted.bind(this, extra),
        );
    };

    handleRemoveExtra = (extra: Extra): void => {
        const { extrasManager } = this.props;

        extrasManager.handleRemove({ extra });
    };

    handleCurrentExtrasVisibilityChange = (isCollapsed: boolean): void => {
        this.setState({
            isCurrentExtrasCollapsed: isCollapsed,
        });
    };

    renderFilterUi = (): React.ReactNode => {
        const { filterQuery, filterChallengeMin, filterChallengeMax, selectedGroup } = this.state;
        const { extrasManager } = this.props;

        let challengeOptions = extrasManager.getChallengeOptions();
        let isSidekickGroup = selectedGroup !== null && extrasManager.hack__isSidekickGroup(selectedGroup);

        return (
            <div className="ct-extra-manage-pane__filters">
                <div className="ct-extra-manage-pane__filter">
                    <div className="ct-extra-manage-pane__filter-heading">Filter</div>
                    <input
                        type="search"
                        className="ct-filter__query"
                        value={filterQuery}
                        onChange={this.handleQueryChange}
                        placeholder="Name, Type, Subtype, Environment, Tag, etc."
                        spellCheck={false}
                        autoComplete="off"
                    />
                </div>
                {!isSidekickGroup && (
                    <div className="ct-extra-manage-pane__filters-challenge">
                        <div className="ct-extra-manage-pane__filters-challenge-heading">Challenge Range</div>
                        <div className="ct-extra-manage-pane__filters-challenge-fields">
                            <div className="ct-extra-manage-pane__filters-challenge-field">
                                <Select
                                    value={filterChallengeMin}
                                    options={challengeOptions}
                                    onChange={this.handleChallengeMinChange}
                                    placeholder="--"
                                />
                            </div>
                            <div className="ct-extra-manage-pane__filters-challenge-sep">--</div>
                            <div className="ct-extra-manage-pane__filters-challenge-field">
                                <Select
                                    value={filterChallengeMax}
                                    options={challengeOptions}
                                    onChange={this.handleChallengeMaxChange}
                                    placeholder="--"
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    renderPagedListing = <T extends any>(
        extras: Array<T>,
        listingComponentPropMappingFunc: (extra: T) => PagedListingProps,
    ): React.ReactNode => {
        const { currentPage } = this.state;
        const { pageSize, isReadonly } = this.props;

        const pagedExtras = extras.slice(0, (currentPage + 1) * pageSize);

        return (
            <div className="ct-extra-manage-pane__listing">
                {pagedExtras.map((extra) => (
                    <ExtraManagePaneAddListing {...listingComponentPropMappingFunc(extra)} isReadonly={isReadonly} />
                ))}
            </div>
        );
    };

    renderCreatures = (): React.ReactNode => {
        const { ruleData, theme } = this.props;

        let filteredCreatures = this.getFilteredCreatures();

        return (
            <div className="ct-extra-manage-pane__extras">
                {this.renderFilterUi()}
                {this.renderPagedListing(filteredCreatures, (creature) => {
                    const sources = CreatureUtils.getSources(creature);
                    const sourceAbbrs = CreatureUtils.isHomebrew(creature)
                        ? [<SourceAbbr isDarkMode={theme.isDarkMode} sourceName="Homebrew" />]
                        : sources
                              .map((sourceMapping) => RuleDataUtils.getSourceDataInfo(sourceMapping.sourceId, ruleData))
                              .filter(TypeScriptUtils.isNotNullOrUndefined)
                              .map((source) => {
                                  return <SourceAbbr sourceName={source.description} isDarkMode={theme.isDarkMode} />;
                              });
                    return {
                        key: CreatureUtils.getId(creature),
                        extra: ExtraUtils.generateCreatureExtra(creature, ruleData),
                        onAdd: this.handleCreateExtra,
                        collapsibleComponentProps: {
                            metaItems: [...ExtraUtils.generateCreatureMeta(creature, ruleData), ...sourceAbbrs],
                            heading: CreatureUtils.getName(creature),
                        },
                        ContentComponent: CreatureBlockBasic,
                        contentComponentProps: {
                            creature: creature,
                            ruleData: ruleData,
                        },
                    };
                })}
                {this.renderPager(filteredCreatures)}
            </div>
        );
    };

    renderVehicles = (): React.ReactNode => {
        const { ruleData } = this.props;

        let vehicles = this.getVehicles();

        return (
            <div className="ct-extra-manage-pane__extras">
                {this.renderPagedListing(vehicles, (vehicle) => {
                    return {
                        key: VehicleUtils.getUniqueKey(vehicle),
                        extra: ExtraUtils.generateVehicleExtra(vehicle, ruleData),
                        onAdd: this.handleCreateExtra,
                        collapsibleComponentProps: {
                            metaItems: ExtraUtils.generateVehicleMeta(vehicle, ruleData),
                            heading: VehicleUtils.getName(vehicle),
                        },
                        ContentComponent: VehicleBlock,
                        contentComponentProps: ComponentUtils.generateVehicleBlockProps(vehicle, ruleData),
                        showQuantity: false,
                    };
                })}
                {this.renderPager(vehicles)}
            </div>
        );
    };

    renderGroupSelector = (): React.ReactNode => {
        const { selectedGroup, filterRules, dataStatus } = this.state;
        const { creatureGroupRulesLookup, extrasManager } = this.props;

        const selectedGroupInfo = extrasManager.getCreatureGroupInfo(selectedGroup);

        let groupRules: Array<CreatureRule> | null =
            selectedGroup === null ? null : HelperUtils.lookupDataOrFallback(creatureGroupRulesLookup, selectedGroup);
        let isSidekickGroup: boolean = selectedGroup !== null && extrasManager.hack__isSidekickGroup(selectedGroup);

        let hasGroupRules: boolean = !!groupRules || isSidekickGroup;
        let showNotDefaultNoRules: boolean =
            selectedGroupInfo !== null && !selectedGroupInfo.enabledByDefault && !hasGroupRules;

        return (
            <div className="ct-extra-manage-pane__selected-group">
                <div className="ct-extra-manage-pane__selected-group-chooser">
                    <Select
                        options={extrasManager.getGroupOptions()}
                        value={selectedGroup}
                        onChange={this.handleSelectedGroupChange}
                        placeholder="-- Choose a Category --"
                        disabled={dataStatus === DataRetrievalStatusEnum.LOADING}
                    />
                </div>
                {selectedGroup && (
                    <div className="ct-extra-manage-pane__selected-group-content">
                        {selectedGroupInfo && selectedGroupInfo.description && (
                            <HtmlContent
                                className="ct-extra-manage-pane__selected-group-description"
                                html={selectedGroupInfo.description}
                            />
                        )}
                        {showNotDefaultNoRules && (
                            <div className="ct-extra-manage-pane__selected-group-warning">
                                Adding a creature of this category will be outside of normal game rules.
                            </div>
                        )}
                        {selectedGroupInfo && hasGroupRules && (
                            <div className="ct-extra-manage-pane__selected-group-rules">
                                <Checkbox
                                    initiallyEnabled={filterRules}
                                    onChange={this.handleRawToggle}
                                    label={`Filter Using ${selectedGroupInfo.name} Rules`}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
        //`
    };

    renderManager = (): React.ReactNode => {
        const { dataStatus, selectedGroup } = this.state;

        if (dataStatus !== DataRetrievalStatusEnum.LOADED) {
            return <LoadingPlaceholder />;
        }

        let extrasNode: React.ReactNode;
        if (selectedGroup !== null) {
            if (selectedGroup === Constants.HACK_VEHICLE_GROUP_ID) {
                extrasNode = this.renderVehicles();
            } else {
                extrasNode = this.renderCreatures();
            }
        }

        return (
            <div className="ct-extra-manage-pane__manager">
                {this.renderGroupSelector()}
                {extrasNode}
            </div>
        );
    };

    renderPager = <T extends any>(filteredItems: Array<T>): React.ReactNode => {
        const { currentPage } = this.state;
        const lastPageIdx = this.getLastPageIdx(filteredItems);

        if (lastPageIdx < 1 || currentPage >= lastPageIdx) {
            return null;
        }

        return (
            <div className="ct-extra-manage-pane__pager">
                <ThemeButton block={true} onClick={this.handlePageMore}>
                    Load More
                </ThemeButton>
            </div>
        );
    };

    renderCurrentExtras = (
        extras: Array<Extra>,
        heading: string | null,
        groupId: number | Constants.ExtraGroupTypeEnum,
    ): React.ReactNode => {
        const { ruleData, isReadonly, theme, snippetData } = this.props;

        if (!extras.length) {
            return null;
        }

        let orderedExtras = orderBy(extras, 'name');

        return (
            <div className="ct-extra-manage-pane__group" key={groupId}>
                <div className="ct-extra-manage-pane__group-heading">{heading}</div>
                <div className="ct-extra-manage-pane__group-items">
                    {orderedExtras.map((extra) => (
                        <ExtraManagePaneCurrentListing
                            theme={theme}
                            key={ExtraUtils.getUniqueKey(extra)}
                            extra={extra}
                            ruleData={ruleData}
                            isReadonly={isReadonly}
                            onRemove={this.handleRemoveExtra}
                            snippetData={snippetData}
                        />
                    ))}
                </div>
            </div>
        );
    };

    renderExtras = (): React.ReactNode => {
        const { extras, extrasManager } = this.props;

        if (!extras.length) {
            return <div className="ct-extra-manage-pane__default">Extras that you add will display here.</div>;
        }

        const groups = ExtraUtils.generateGroups(extras);
        const orderedCreatureGroupInfos = extrasManager.getCurrentGroupInfos(extras);

        return (
            <div className="ct-extra-manage-pane__inventory">
                {orderedCreatureGroupInfos.map((groupInfo) =>
                    this.renderCurrentExtras(groups[groupInfo.id], groupInfo.name, groupInfo.id),
                )}
            </div>
        );
    };

    render() {
        const { isCurrentExtrasCollapsed } = this.state;

        return (
            <div className="ct-extra-manage-pane">
                <SidebarHeader>Manage Extras</SidebarHeader>
                <Collapsible header="Add an Extra" initiallyCollapsed={false}>
                    {this.renderManager()}
                </Collapsible>
                <Collapsible
                    header="Current Extras"
                    collapsed={isCurrentExtrasCollapsed}
                    onChangeHandler={this.handleCurrentExtrasVisibilityChange}
                >
                    {this.renderExtras()}
                </Collapsible>
            </div>
        );
    }
}

function mapStateToProps(state: SharedAppState) {
    return {
        definitionPool: serviceDataSelectors.getDefinitionPool(state),
        loadAvailableMonsters: apiCreatorSelectors.makeLoadAvailableMonsters(state),
        loadAvailableVehicles: apiCreatorSelectors.makeLoadAvailableVehicles(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        creatureRules: rulesEngineSelectors.getCreatureRules(state),
        creatureGroupRulesLookup: rulesEngineSelectors.getCreatureGroupRulesLookup(state),
        isReadonly: appEnvSelectors.getIsReadonly(state),
        snippetData: rulesEngineSelectors.getSnippetData(state),
        theme: rulesEngineSelectors.getCharacterTheme(state),
        extras: rulesEngineSelectors.getExtras(state),
    };
}

function ExtraManagePaneContainer(props) {
    const { extrasManager } = useContext(ExtrasManagerContext);
    return <ExtraManagePane extrasManager={extrasManager} {...props} />;
}

export default connect(mapStateToProps)(ExtraManagePaneContainer);
